'use client'

import { useEffect, useState } from 'react';
import { useAppKit, useAppKitAccount } from '@reown/appkit/react';
import styles from '../styles/Web3Button.module.css';
import { useAuth } from '../../context/AuthContext';

interface ConnectButtonProps {
  userId?: string | null;
  dbUserWalletAddress?: string | null;
}

export const ConnectButton: React.FC<ConnectButtonProps> = ({ userId, dbUserWalletAddress }) => {
  const { open } = useAppKit();
  const { isConnected, address: appKitAddress } = useAppKitAccount();
  const { checkWalletAuth } = useAuth();
  const [initialized, setInitialized] = useState(false);
  const [isUpdatingWallet, setIsUpdatingWallet] = useState(false);
  
  useEffect(() => {
    setInitialized(true);
  }, []);

  useEffect(() => {
    const handleWalletConnection = async () => {
      if (isConnected && appKitAddress && userId && !dbUserWalletAddress) {
        console.log('ConnectButton: Wallet connected, attempting to update user record.');
        setIsUpdatingWallet(true);
        try {
          const response = await fetch('/api/user/update-wallet', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ userId: userId, walletAddress: appKitAddress }),
          });
          const data = await response.json();
          if (data.success) {
            console.log('ConnectButton: Wallet address successfully updated in DB.');
            await checkWalletAuth();
          } else {
            console.error('ConnectButton: Failed to update wallet address in DB:', data.message);
          }
        } catch (error) {
          console.error('ConnectButton: Error calling update-wallet API:', error);
        } finally {
          setIsUpdatingWallet(false);
        }
      } else if (isConnected && appKitAddress) {
        console.log('ConnectButton: AppKit wallet connected. User ID:', userId, 'DB Wallet:', dbUserWalletAddress);
        if (!isUpdatingWallet) {
          checkWalletAuth();
        }
      }
    };

    if (initialized) {
      handleWalletConnection();
    }
  }, [isConnected, appKitAddress, userId, dbUserWalletAddress, initialized, checkWalletAuth]);

  useEffect(() => {
    if (initialized) {
      if (isConnected && appKitAddress) {
        console.log('ConnectButton: AppKit connected with address:', appKitAddress);
      } else {
        console.log('ConnectButton: AppKit not connected or no address.');
      }
    }
  }, [isConnected, appKitAddress, initialized]);

  if (!initialized || isUpdatingWallet) {
    return (
      <div className={styles.buttonContainer}>
        <button 
          className={styles.web3button}
          disabled
        >
          <div className={styles.buttonContainer}>
            <span className={styles.walletIcon}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="animate-spin">
                <path d="M12 2.99982C16.9706 2.99982 21 7.02925 21 11.9998C21 16.9704 16.9706 20.9998 12 20.9998C7.02944 20.9998 3 16.9704 3 11.9998C3 9.17264 4.30367 6.64992 6.34267 4.99982" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <p>{isUpdatingWallet ? 'Updating Wallet...' : 'Loading...'}</p>
          </div>
        </button>
      </div>
    );
  }

  return (
    <div>
      {isConnected ? (
        <div className={styles.buttonContainer}>
          <button 
            onClick={() => open({ view: 'Account' })}
            className={styles.profileLink}
          >
            <span className={styles.walletIcon}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.273 5.625A4.483 4.483 0 0 1 5.25 4.5h13.5c1.141 0 2.183.425 2.977 1.125A3 3 0 0 0 18.75 3H5.25a3 3 0 0 0-2.977 2.625ZM2.273 8.625A4.483 4.483 0 0 1 5.25 7.5h13.5c1.141 0 2.183.425 2.977 1.125A3 3 0 0 0 18.75 6H5.25a3 3 0 0 0-2.977 2.625ZM5.25 9a3 3 0 0 0-3 3v6a3 3 0 0 0 3 3h13.5a3 3 0 0 0 3-3v-6a3 3 0 0 0-3-3H15a.75.75 0 0 0-.75.75 2.25 2.25 0 0 1-4.5 0A.75.75 0 0 0 9 9H5.25Z" />
              </svg>
            </span>
            {appKitAddress?.slice(0, 6)}...{appKitAddress?.slice(-4)}
          </button>
        </div>
      ) : (
        <button 
          onClick={() => open()}
          className={styles.web3button}
        >
          <div className={styles.buttonContainer}>
            <span className={styles.walletIcon}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.273 5.625A4.483 4.483 0 0 1 5.25 4.5h13.5c1.141 0 2.183.425 2.977 1.125A3 3 0 0 0 18.75 3H5.25a3 3 0 0 0-2.977 2.625ZM2.273 8.625A4.483 4.483 0 0 1 5.25 7.5h13.5c1.141 0 2.183.425 2.977 1.125A3 3 0 0 0 18.75 6H5.25a3 3 0 0 0-2.977 2.625ZM5.25 9a3 3 0 0 0-3 3v6a3 3 0 0 0 3 3h13.5a3 3 0 0 0 3-3v-6a3 3 0 0 0-3-3H15a.75.75 0 0 0-.75.75 2.25 2.25 0 0 1-4.5 0A.75.75 0 0 0 9 9H5.25Z" />
              </svg>
            </span>
            <p>Connect Wallet</p>
          </div>
        </button>
      )}
    </div>
  );
}

